import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  styled,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import ChevronLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRight from '@mui/icons-material/KeyboardDoubleArrowRight';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const State = () => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [ShowUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchTerm, setSearchTerm] = useState('');
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [canAdd, setCanAdd] = useState(true);
  const [selectedState, setSelectedState] = useState({ stateId: null, stateName: '', countryName: '', countryId: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddStateModal, setShowAddStateModal] = useState(false);
  const [showEditStateModal, setShowEditStateModal] = useState(false);
  const [countryId, setCountryId] = useState('');
  const [stateName, setStateName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const [countryData, setCountryData] = useState([]);
  const [states, setStates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      // Map the API response to match your component's expected structure
      const mappedCountries = response.data.map(country => ({
        countryId: country.id,  // Use `id` from the API response
        countryName: country.name,
      }));
      setCountryData(mappedCountries);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch countries.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };


  // Fetch states
  // Fetch states
  const fetchStates = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/states/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      // Map the API response to match your component's expected structure
      const mappedStates = response.data.map(state => ({
        stateId: state.id,
        stateName: state.name,
        countryId: state.country,  // Use `country` as the `countryId`
        countryName: countryData.find(country => country.countryId === state.country)?.countryName || 'Unknown',
        //                                       ^^^^^^^^^ changed from 'id' to 'countryId'
        //                                                                        ^^^^^^^^^^^ changed from 'name' to 'countryName'
      }));
      setStates(mappedStates);
      setFilteredData(mappedStates);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch states.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };





  // Add a new state
  const handleClick = () => {
    setCountryId('');
    setStateName('');
    setShowAddStateModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!stateName || !countryId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('State name and country are required.');
      setOpenSnackbar(true);
      return;
    }

    // Check if the selected countryId exists in countryData
    const selectedCountry = countryData.find(country => country.countryId === parseInt(countryId));
    if (!selectedCountry) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Selected country is invalid.');
      setOpenSnackbar(true);
      return;
    }

    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/master/states/`,
        { name: stateName, country: countryId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      const newState = {
        stateId: response.data.id,
        stateName: response.data.name,
        countryId: response.data.country,
        countryName: countryData.find(country => country.countryId === response.data.country)?.countryName || 'Unknown',
      };

      setStates([...states, newState]);
      setFilteredData([...filteredData, newState]);
      setShowAddStateModal(false);
      setSnackbarMessage('State added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setStateName('');
      setCountryId('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');

      // Check if the error response contains the name unique constraint message
      if (err.response?.data?.name && err.response.data.name[0] === "State name must be unique within this country.") {
        setSnackbarMessage('State name must be unique within this country.');
      } else {
        setSnackbarMessage('Failed to add state.');
      }

      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a state
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/states/${selectedState.stateId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const updatedStates = states.filter((state) => state.stateId !== selectedState.stateId);
      setStates(updatedStates);
      setFilteredData(updatedStates);
      setShowDeleteModal(false);
      setSnackbarMessage('State deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete state.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Edit a state
  const handleEdit = (state) => {
    setSelectedState(state);
    setStateName(state.stateName);
    setCountryId(state.countryId);
    setShowEditStateModal(true);
  };

  const handleUpdate = async () => {
    if (!stateName || !countryId) {
      setSnackbarSeverity('error');
      setSnackbarMessage('State name and country are required.');
      setOpenSnackbar(true);
      return;
    }
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/master/states/${selectedState.stateId}/`,
        { name: stateName, country: countryId },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      // Map the response to match your component's expected structure
      const updatedState = {
        stateId: response.data.id,
        stateName: response.data.name,
        countryId: response.data.country,
        countryName: countryData.find(country => country.countryId === response.data.country)?.countryName || 'Unknown',
      };

      const updatedStates = states.map((state) =>
        state.stateId === selectedState.stateId ? updatedState : state
      );
      setStates(updatedStates);
      setFilteredData(updatedStates);
      setShowEditStateModal(false);
      setSnackbarMessage('State updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');

      // Check if the error response contains the name unique constraint message
      if (err.response?.data?.name && err.response.data.name[0] === "State name must be unique within this country.") {
        setSnackbarMessage('State name must be unique within this country.');
      } else {
        setSnackbarMessage('Failed to update state.');
      }

      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  const handleShowDeleteModal = (stateId, stateName, countryName, countryId) => {
    setSelectedState({ stateId, stateName, countryName, countryId });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleReset = () => {
    setStateName('');
    setCountryId('');
  };

  const handleCancel = () => {
    setShowEditStateModal(false);
  };

  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      // Fetch countries first, then fetch states after countries are loaded
      const fetchData = async () => {
        try {
          // Fetch countries
          const accessToken = localStorage.getItem('accessToken');
          const countryResponse = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });

          // Map the API response to match your component's expected structure
          const mappedCountries = countryResponse.data.map(country => ({
            countryId: country.id,
            countryName: country.name,
          }));
          setCountryData(mappedCountries);

          // Now fetch states using the mappedCountries data
          const stateResponse = await axios.get(`${API_BASE_URL}/api/master/states/`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });

          // Map the API response and use mappedCountries to get country names
          const mappedStates = stateResponse.data.map(state => ({
            stateId: state.id,
            stateName: state.name,
            countryId: state.country,
            countryName: mappedCountries.find(country => country.countryId === state.country)?.countryName || 'Unknown',
          }));

          setStates(mappedStates);
          setFilteredData(mappedStates);
        } catch (err) {
          setError(err.message);
          setSnackbarSeverity('error');
          setSnackbarMessage('Failed to fetch data.');
          setOpenSnackbar(true);
          if (err.response?.status === 401) {
            localStorage.removeItem('accessToken');
            navigate('/login');
          }
        } finally {
          setLoading(false);
        }
      };

      setLoading(true);
      fetchData();
    }
  }, [navigate]);


  useEffect(() => {
    setFilteredData(
      states.filter((state) =>
        state.stateName?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, states]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by State Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Tooltip title={!canAdd && 'You are not Authorized to Add'} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleClick}
                sx={{
                  ml: 2,
                  width: '120px',
                  height: '50px',
                  padding: '10px 0',
                  fontWeight: 'bold',
                  borderRadius: '10px',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
                startIcon={<AddIcon />}
                disabled={!canAdd}
              >
                ADD STATE
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Country Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>State Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((state, index) => (
                    <TableRow
                      key={state.stateId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{state.countryName}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{state.stateName}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={canEdit ? '' : 'You are not authorized to edit'} arrow>
                          <span>
                            <IconButton
                              onClick={() => handleEdit(state)}
                              disabled={!canEdit}
                              sx={{
                                color: theme.palette.primary.light,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={canDelete ? '' : 'You are not authorized to delete'} arrow>
                          <span>
                            <IconButton
                              color="error"
                              onClick={() => handleShowDeleteModal(state.stateId, state.stateName, state.countryName, state.countryId)}
                              disabled={!canDelete}
                              sx={{
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={4} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>
      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedState.stateName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add State Modal */}
      <Modal open={showAddStateModal} onClose={() => setShowAddStateModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add State
            </Typography>
            <IconButton onClick={() => setShowAddStateModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="country"
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}>Country</InputLabel>
              <Select
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
              >
                {countryData.map((countryItem) => (
                  <MenuItem key={countryItem.countryId} value={countryItem.countryId}>
                    {countryItem.countryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="State Name"
              variant="outlined"
              fullWidth
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!countryId ? "Select Country" : !stateName ? "Enter State Name" : ""} arrow>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark }
                  }}
                  disabled={!stateName || !countryId}
                >
                  SAVE
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Edit State Modal */}
      <Modal open={showEditStateModal} onClose={handleCancel} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit State
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel htmlFor="country"
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}>Country</InputLabel>
              <Select
                id="country"
                value={countryId}
                label="Country"
                onChange={(e) => setCountryId(e.target.value)}
              >
                {countryData.map((countryItem) => (
                  <MenuItem key={countryItem.countryId} value={countryItem.countryId}>
                    {countryItem.countryName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="State Name"
              variant="outlined"
              fullWidth
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              sx={{
                mb: 3,
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: theme.palette.primary.main,
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={!stateName && "Enter State Name"} arrow>
                <Button
                  variant="contained"
                  onClick={handleUpdate}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark }
                  }}
                  disabled={!stateName}
                >
                  UPDATE
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancel}
              >
                CANCEL
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* User Guide Modal */}
      <Modal
        open={ShowUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 800,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              State Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              ➤ Follow these steps to add a new state:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Click the <strong>"ADD STATE"</strong> button at the top right to open the Add State dialog.
              </li>
              <li>
                Select the <strong>Country</strong> from the dropdown menu.
              </li>
              <li>
                Enter the <strong>State Name</strong> in the provided text field.
              </li>
              <li>
                Before clicking <strong>"SAVE"</strong>, ensure all required fields are completed.
              </li>
              <li>
                Click <strong>"SAVE"</strong> to add the state. A success notification will confirm the addition.
              </li>
              <li>
                The newly added state will appear in the State List table.
              </li>
              <li>
                To clear the form without saving, click <strong>"RESET"</strong>.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Country and State Name are mandatory fields.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to update an existing state:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Locate the state you want to update in the State List table.
              </li>
              <li>
                Click the <strong>Edit</strong> icon (pencil) in the Action column to open the Edit State dialog.
              </li>
              <li>
                Update the <strong>State Name</strong> as required.
              </li>
              <li>
                Before clicking <strong>"UPDATE"</strong>, verify that all fields are filled correctly.
              </li>
              <li>
                Click <strong>"UPDATE"</strong> to save changes. A success notification will be shown.
              </li>
              <li>
                To exit without saving, click <strong>"CANCEL"</strong>.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: State Name is a required field (Country selection is also needed).
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to delete a state:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Find the state you want to delete in the State List table.
              </li>
              <li>
                Click the <strong>Delete</strong> icon (trash) in the Action column.
              </li>
              <li>
                Confirm the deletion by clicking <strong>"Yes, Delete"</strong> in the confirmation dialog.
              </li>
              <li>
                The state will be removed from the list, and a success notification will appear.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: Deleted states cannot be recovered. Proceed with caution.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to search for a state:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the search bar at the top left of the State List.
              </li>
              <li>
                Enter the State Name (or part of it) to filter the table.
              </li>
              <li>
                The table will automatically update to show matching states.
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The search is case-insensitive and filters by State Name.
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Follow these steps to navigate the state list:
            </Typography>
            <ul
              style={{
                marginLeft: '20px',
                lineHeight: '1.8',
                cursor: 'pointer',
              }}
            >
              <li>
                Use the pagination controls at the bottom to navigate through pages of states.
              </li>
              <li>
                Click the left or right arrows to move between pages.
              </li>
              <li>
                Select a specific page number to jump directly to that page.
              </li>
              <li>
                Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
              ⚠️ Note: The table displays only the selected number of rows per page.
            </Typography>
          </CustomTableScrollbar>
        </Box>
      </Modal>

    </Container>
  );
};

export default State;